/**
 * LinkTargetDocumentModal - Select a target document for a plan
 * 
 * Shows a list of project documents that the plan can target.
 * Filters out the plan document itself.
 */

import { useState, useEffect } from 'react';
import { projectsApi } from '../../services/api';
import { 
  useToast, 
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui';
import { Loader2, FileText, CheckCircle } from 'lucide-react';

interface ProjectDocument {
  documentNumber: string;
  documentTitle?: string;
  title?: string;
  status?: string;
  currentRevision?: string;
  docType?: string;
}

interface LinkTargetDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (document: { id: string; name: string }) => void;
  projectId: string;
  excludeDocumentNumber: string; // The plan document itself
}

export default function LinkTargetDocumentModal({
  isOpen,
  onClose,
  onSelect,
  projectId,
  excludeDocumentNumber,
}: LinkTargetDocumentModalProps) {
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ProjectDocument | null>(null);
  const { error } = useToast();

  useEffect(() => {
    if (isOpen && projectId) {
      loadDocuments();
    }
  }, [isOpen, projectId]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const docs = await projectsApi.getDocuments(projectId);
      // Filter out plan documents and the current plan itself
      const filteredDocs = docs.filter((doc: ProjectDocument) => 
        doc.documentNumber !== excludeDocumentNumber &&
        !doc.documentNumber.startsWith('PLAN-')
      );
      setDocuments(filteredDocs);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load documents';
      error('Load Failed', message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedDocument) {
      onSelect({
        id: selectedDocument.documentNumber,
        name: selectedDocument.documentTitle || selectedDocument.title || selectedDocument.documentNumber,
      });
      onClose();
      setSelectedDocument(null);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedDocument(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Link Target Document</DialogTitle>
          <DialogDescription>
            Select the document that this plan will modify when executed.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading documents...</span>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No documents available in this project.</p>
              <p className="text-xs mt-1">Create a document first, then link it to this plan.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2">
              {documents.map((doc) => {
                const isSelected = selectedDocument?.documentNumber === doc.documentNumber;
                const title = doc.documentTitle || doc.title || 'Untitled';
                
                return (
                  <div
                    key={doc.documentNumber}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedDocument(doc)}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedDocument(doc)}
                    className={`w-full text-left p-3 rounded-md border transition-colors cursor-pointer ${
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {isSelected ? (
                          <CheckCircle className="h-5 w-5 text-primary" />
                        ) : (
                          <FileText className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {doc.documentNumber}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {title}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {doc.docType && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              {doc.docType}
                            </span>
                          )}
                          {doc.status && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              doc.status === 'Released' ? 'bg-success-light text-success' :
                              doc.status === 'Approved' ? 'bg-info-light text-info' :
                              doc.status === 'In Review' ? 'bg-warning-light text-warning' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {doc.status}
                            </span>
                          )}
                          {doc.currentRevision && (
                            <span className="text-xs text-muted-foreground">
                              Rev {doc.currentRevision}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleClose} variant="outline">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!selectedDocument}
          >
            Link Document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
